// 결제 지갑 → 인게임 닉네임(Name#0000) 조회.
//
// 닉네임은 V8 프로필이 아니라 각 verse 의 게임 유저 상태(users 컬렉션)에 있고,
// 문서 키는 EIP-55 체크섬 주소다 — 소문자로 조회하면 서버가 조용히 빈 상태를 돌려준다
// (에러가 아니라 빈 객체라 눈치채기 어렵다). 그래서 항상 체크섬으로 변환해 조회한다.
// verse 별로 저장소가 분리되므로 web2 payer 는 web2 verse 에서만 찾을 수 있다.

import { isEthereumAddress, toChecksumAddress } from '@rb/shared/eip55';
import { formatNickname } from '@rb/shared/nickname';

/** 게임 유저 상태에서 표시용 닉네임을 만든다. 닉네임이 없으면(미생성 유저) null. */
export function gameNicknameOf(state: Record<string, unknown> | null | undefined): string | null {
  const nickname = typeof state?.nickname === 'string' ? state.nickname : '';
  if (!nickname) return null;
  const hashCode = typeof state?.nicknameHashCode === 'string' ? state.nicknameHashCode : '';
  return hashCode ? formatNickname(nickname, hashCode) : nickname;
}

export interface NicknameTarget {
  verseId: string;
  wallet: string;
}

/** 조회 결과 맵 키 — 같은 지갑이라도 verse 마다 다른 유저이므로 verse 를 포함한다. */
export const nicknameKey = (verseId: string, wallet: string): string =>
  `${verseId}|${wallet.toLowerCase()}`;

export type UserStateFetcher = (
  verseId: string,
  account: string
) => Promise<Record<string, unknown> | null>;

export interface ResolveOptions {
  /** 호출 간 간격. V8 SDK 는 함수명당 초당 10회 제한이라 기본 120ms 로 띄운다. */
  delayMs?: number;
  onProgress?: (p: { done: number; total: number }) => void;
  signal?: AbortSignal;
}

export interface ResolveResult {
  /** nicknameKey → "Name#0000". 상태가 없거나 닉네임 미설정이면 키 자체가 없다. */
  nicknames: Map<string, string>;
  /** 조회 실패(RPC 에러) 건수. 부분 실패해도 나머지는 계속 조회한다. */
  failed: number;
}

const DEFAULT_DELAY_MS = 120;

const sleep = (ms: number): Promise<void> =>
  ms > 0 ? new Promise((r) => setTimeout(r, ms)) : Promise.resolve();

/**
 * 대상 (verse, 지갑) 들의 인게임 닉네임을 순차 조회한다.
 * 순차인 이유는 V8 SDK 의 함수명당 rate limit — Promise.all 로 던지면 429 가 난다.
 */
export async function resolveGameNicknames(
  targets: NicknameTarget[],
  fetchState: UserStateFetcher,
  opts: ResolveOptions = {}
): Promise<ResolveResult> {
  const delayMs = opts.delayMs ?? DEFAULT_DELAY_MS;
  const nicknames = new Map<string, string>();
  let failed = 0;

  // (verse, 지갑) 중복 제거 + 지갑 주소가 아닌 항목(uid 만 있는 payer) 제외.
  const unique = new Map<string, NicknameTarget>();
  for (const t of targets) {
    if (!t.wallet || !isEthereumAddress(t.wallet)) continue;
    const key = nicknameKey(t.verseId, t.wallet);
    if (!unique.has(key)) unique.set(key, t);
  }

  const total = unique.size;
  let done = 0;
  let first = true;
  for (const [key, t] of unique) {
    if (opts.signal?.aborted) break;
    if (!first) await sleep(delayMs);
    first = false;
    try {
      const state = await fetchState(t.verseId, toChecksumAddress(t.wallet));
      const nickname = gameNicknameOf(state);
      if (nickname) nicknames.set(key, nickname);
    } catch {
      // 개별 실패는 삼키고 계속 — 한 명 때문에 표 전체가 비지 않게.
      failed++;
    }
    done++;
    opts.onProgress?.({ done, total });
  }

  return { nicknames, failed };
}
